import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Button, Stack } from "@mui/material";
import type {
  GridAutoGeneratedGroupNode,
  GridDataGroupNode,
  GridLeafNode,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD, useGridApiRef } from "@mui/x-data-grid-premium";
import deepEqual from "fast-deep-equal";
import type { Dispatch, JSX, PropsWithChildren, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError, ApiResponse } from "@entrilia/shared/api/types";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { distinctBy, groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import { checkSelection, setAllNonGroupRowsSelection } from "@entrilia/shared/utilities/dataGridApiHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { Category } from "../../../../api/types/accessTypes";
import type {
  InvestorCommunicationMatrixCreate,
  InvestorCommunicationMatrixRemove,
  InvestorCommunicationMatrixUpdate,
  UpdateInvestorCommunicationMatrixRequest,
} from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import usePageLeaveConfirmation from "../../../../hooks/usePageLeaveConfirmation";
import ConcurrentModificationWarningDialog from "../../../common/ConcurrentModificationWarningDialog";
import NewLabel from "../../../common/NewLabel";
import DataGridGroupingCell from "../../../common/grid/DataGridGroupingCell";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import SendInvitationsConfirmationDialog from "../common/SendInvitationsConfirmationDialog";
import CommunicationMatrixFilters from "./CommunicationMatrixFilters";
import MatrixBulkEdit from "./MatrixBulkEdit";
import { getCommunicationMatrixColumns } from "./matrixColumnDefinitions";
import type {
  BulkPermissionsState,
  CommunicationMatrixEntityType,
  CommunicationMatrixModel,
  ContactPermissionsChange,
} from "./matrixHelper";
import {
  emptyGuid,
  getBulkPermissionsState,
  getModelId,
  isEmptyFundInvestor,
  isEmptyInvestorContact,
  updateSelectedModelsWithPermissions,
} from "./matrixHelper";

interface Props {
  entityType: CommunicationMatrixEntityType;
  noItemsMessage: string;
  renderGroupCellElement: (
    value: unknown,
    node: GridDataGroupNode | GridAutoGeneratedGroupNode | GridLeafNode
  ) => JSX.Element;
  renderGroupedRowCell: (model: CommunicationMatrixModel) => JSX.Element;
  renderGridToolbarComponent?: (isLoading: boolean) => JSX.Element;
  categories: Category[];
  initialModels: CommunicationMatrixModel[];
  setInitialModels: Dispatch<SetStateAction<CommunicationMatrixModel[]>>;
  models: CommunicationMatrixModel[];
  setModels: Dispatch<SetStateAction<CommunicationMatrixModel[]>>;
  getChangesWhereInvitationWouldBeSent: (
    originalPermissionModels: CommunicationMatrixModel[],
    modifiedPermissionModels: CommunicationMatrixModel[]
  ) => ContactPermissionsChange[];
  sortModelFunc: (a: CommunicationMatrixModel, b: CommunicationMatrixModel) => number;
  getTreeDataPath: (model: CommunicationMatrixModel) => string[];
  cloneModelWithoutChildren: (model: CommunicationMatrixModel) => CommunicationMatrixModel;
  childRowActionsDisabled?: boolean;
  groupRowActionsDisabled?: boolean;
  updateInvestorCommunicationMatrix: (
    payload: UpdateInvestorCommunicationMatrixRequest
  ) => Promise<ApiResponse<CommunicationMatrixModel[] | void>>;
  excludedColumns?: string[];
}

const CommunicationMatrix = ({
  entityType,
  noItemsMessage,
  categories,
  initialModels,
  setInitialModels,
  models,
  setModels,
  renderGridToolbarComponent,
  renderGroupCellElement,
  getChangesWhereInvitationWouldBeSent,
  sortModelFunc,
  getTreeDataPath,
  cloneModelWithoutChildren,
  renderGroupedRowCell,
  childRowActionsDisabled,
  groupRowActionsDisabled,
  updateInvestorCommunicationMatrix,
  excludedColumns,
}: Props) => {
  const gridApi = useGridApiRef();
  const { hasAnyPermission } = useClientContext();
  const hasAccessToManage = hasAnyPermission(["ManageFundStructure"]);
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { setPromptOnPageLeave } = usePageLeaveConfirmation(false);

  const [filteredModels, setFilteredModels] = useState<CommunicationMatrixModel[]>(() =>
    structuredClone(initialModels)
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [invitationChanges, setInvitationChanges] = useState<ContactPermissionsChange[]>([]);
  const [bulkPermissionsState, setBulkPermissionsState] = useState<BulkPermissionsState>();
  const [concurrentModificationMessage, setConcurrentModificationMessage] = useState<string>();
  const { isGroupExpanded } = useGridGroupsExpanded(gridApi);

  const [investorPortalSettings] = useFetch(adminApi.getInvestorPortalSettings);

  const updateCommunicationMatrix = withErrorHandling(updateInvestorCommunicationMatrix);

  const getModelGroupingKey = useCallback(
    (model: CommunicationMatrixModel) => getTreeDataPath(model)[0] ?? "",
    [getTreeDataPath]
  );

  const handleModelChange = useCallback(
    (model: CommunicationMatrixModel) => {
      setModels((currentModels) =>
        currentModels.map((currentModel) => {
          if (getModelId(currentModel) === getModelId(model)) {
            return model;
          }
          return { ...currentModel };
        })
      );
    },
    [setModels]
  );

  const handleModelsRemove = useCallback(
    (ids: string[]) => {
      setModels((currentModels) => {
        if (!groupRowActionsDisabled) {
          return currentModels.filter((m) => !ids.includes(getModelId(m)));
        }

        // When groupRowActionsDisabled is true, and all child models get removed, keep empty group row models
        const newModels: CommunicationMatrixModel[] = [];
        const groupedModels = groupByToMap(currentModels, (m) => getModelGroupingKey(m));

        groupedModels.forEach((group) => {
          const notRemovedFromGroup = group.filter((m) => !ids.includes(getModelId(m)));
          if (notRemovedFromGroup.length > 0) {
            newModels.push(...notRemovedFromGroup);
            return;
          }

          if (group.length === 1) {
            const singleModel = defined(group[0]);
            if (getTreeDataPath(singleModel).length < 2) {
              newModels.push(singleModel);
              return;
            }
          }

          if (group[0]) {
            const emptyGroupModel = cloneModelWithoutChildren(group[0]);
            newModels.push(emptyGroupModel);
          }
        });

        return newModels;
      });
    },
    [cloneModelWithoutChildren, getModelGroupingKey, getTreeDataPath, groupRowActionsDisabled, setModels]
  );

  const rowEditingDisabled = (model: CommunicationMatrixModel) => !model.contactEmail;

  const isNewlyAdded = useCallback(
    (ids: string[]) => ids.some((id) => !initialModels.some((m) => getModelId(m) === id)),
    [initialModels]
  );

  const gridColumns = useMemo(
    () =>
      getCommunicationMatrixColumns({
        messageCategories: categories,
        excludedColumns,
        onChange: handleModelChange,
        onRemove: handleModelsRemove,
        readOnly: !hasAccessToManage,
        rowEditingDisabled,
        childRowActionsDisabled,
        groupRowActionsDisabled,
      }),
    [
      categories,
      excludedColumns,
      handleModelChange,
      handleModelsRemove,
      hasAccessToManage,
      childRowActionsDisabled,
      groupRowActionsDisabled,
    ]
  );

  const handleFilterChange = useCallback(
    (filteredRows: CommunicationMatrixModel[]) => {
      setFilteredModels(filteredRows.sort(sortModelFunc));
    },
    [sortModelFunc]
  );

  const changesExist = useMemo(() => !deepEqual(initialModels, models), [initialModels, models]);

  useEffect(() => {
    setPromptOnPageLeave(changesExist);
  }, [changesExist, setPromptOnPageLeave]);

  const showToolbar = selectedIds.length > 0 && hasAccessToManage;
  const fullSelection = checkSelection(gridApi).checked;

  const handleCancelChanges = () => {
    setModels(structuredClone(initialModels));
    setFilteredModels(structuredClone(initialModels));
  };

  const isConcurrentModificationError = (error: ApiError) =>
    error.type === "ConcurrentModification" || error.code === 409;

  const handleConcurrentModification = (error: ApiError) => {
    const message =
      error.message ||
      "This data was just modified by another user. Please refresh the page to see the latest changes.";
    setConcurrentModificationMessage(message);
    sendNotificationError(message);
  };

  const handleConcurrentModificationClose = () => {
    setConcurrentModificationMessage(undefined);
  };

  const handleSaveChanges = async () => {
    setInvitationChanges([]);

    const updatedModels = models.filter((model) => {
      const initialModel = initialModels.find((m) => getModelId(m) === getModelId(model));
      return !deepEqual(initialModel, model) && !isEmptyFundInvestor(model) && !isEmptyInvestorContact(model);
    });

    const removedModels: InvestorCommunicationMatrixRemove[] = initialModels.filter(
      (m) => !models.some((model) => getModelId(model) === getModelId(m))
    );

    const createdModels = models.filter((model) => isEmptyFundInvestor(model));

    if (updatedModels.length + removedModels.length + createdModels.length === 0) {
      setInitialModels(structuredClone(models));
      return;
    }

    setIsLoading(true);

    const updates: InvestorCommunicationMatrixUpdate[] = updatedModels.map((model) => ({
      fundInvestorId: model.fundInvestorId,
      contactId: model.contactId,
      isPrimary: model.isPrimary,
      emailNotification: model.emailNotification,
      roles: model.roles,
      categories: model.categories,
    }));

    const removes: InvestorCommunicationMatrixRemove[] = removedModels.map((m) => ({
      fundInvestorId: m.fundInvestorId,
      contactId: m.contactId,
    }));

    const creates: InvestorCommunicationMatrixCreate[] = createdModels.map((m) => ({
      investorId: m.investorId,
      investorName: m.investorName,
      contactId: m.contactId === emptyGuid ? undefined : m.contactId,
      isPrimary: m.isPrimary,
      emailNotification: m.emailNotification,
      roles: m.roles,
      categories: m.categories,
    }));

    const [modelsAfterUpdate, error] = await updateCommunicationMatrix({ updates, removes, creates });
    setIsLoading(false);

    if (error) {
      if (isConcurrentModificationError(error)) {
        handleConcurrentModification(error);
        return;
      }
      sendNotificationError(error.message);
      logError(error, "[CommunicationMatrix] updateCommunicationMatrix");
      return;
    }

    sendNotification("Changes saved successfully");
    if (modelsAfterUpdate) {
      setModels(structuredClone(modelsAfterUpdate));
      setInitialModels(structuredClone(modelsAfterUpdate));
    } else {
      setInitialModels(structuredClone(models));
    }
  };

  const handleSave = async () => {
    const changes: ContactPermissionsChange[] = getChangesWhereInvitationWouldBeSent(initialModels, models);
    if (changes.length > 0 && investorPortalSettings?.settings.enableAutomaticInvites) {
      setInvitationChanges(distinctBy(changes, (c) => c.contact.id));
    } else {
      await handleSaveChanges();
    }
  };

  const handleRemove = () => {
    handleModelsRemove(selectedIds);
    setSelectedIds([]);
  };

  const handleBulkEditSubmit = (permissionsState: BulkPermissionsState) => {
    setModels((currentModels) => updateSelectedModelsWithPermissions(currentModels, selectedIds, permissionsState));
    deselectSelectedIds();
  };

  const handleBulkEditClick = () => {
    const selectedModels = models.filter((m) => selectedIds.includes(getModelId(m)));
    setBulkPermissionsState(getBulkPermissionsState(selectedModels, categories));
  };

  const deselectSelectedIds = () => {
    gridApi.current?.selectRows(selectedIds, false, true);
    setSelectedIds([]);
  };

  const selectedModels = models.filter((m) => selectedIds.includes(getModelId(m)));
  const disableBulkEditToolbarButton = selectedModels.every(isEmptyInvestorContact);
  const disableRemoveToolbarButton = groupRowActionsDisabled && selectedModels.every(isEmptyInvestorContact);

  return (
    <>
      <CommunicationMatrixFilters entityType={entityType} permissions={models} onFilterChange={handleFilterChange}>
        {changesExist && (
          <>
            <Button disabled={isLoading} onClick={handleCancelChanges} variant="text" color="secondary">
              Cancel
            </Button>
            <Button disabled={isLoading} onClick={handleSave} variant="contained" color="primary">
              Save Changes
            </Button>
          </>
        )}
        {!changesExist && hasAccessToManage && renderGridToolbarComponent?.(isLoading)}
      </CommunicationMatrixFilters>
      <DataGridHeaderToolbarProvider
        showToolbar={showToolbar}
        fullSelection={fullSelection}
        selection={[selectedIds, () => setAllNonGroupRowsSelection(gridApi, false)]}
        actions={
          <>
            <Button
              disabled={isLoading || disableBulkEditToolbarButton}
              onClick={handleBulkEditClick}
              color="secondary"
              variant="text"
              startIcon={<EditIcon />}
            >
              Bulk Edit
            </Button>
            <Button
              disabled={isLoading || disableRemoveToolbarButton}
              onClick={handleRemove}
              color="error"
              variant="text"
              startIcon={<DeleteIcon />}
            >
              Remove
            </Button>
          </>
        }
      >
        <DataGrid<CommunicationMatrixModel>
          apiRef={gridApi}
          checkboxSelection={hasAccessToManage}
          treeData
          loading={isLoading}
          getTreeDataPath={getTreeDataPath}
          rows={filteredModels}
          getRowId={getModelId}
          columns={gridColumns}
          multilineRows
          groupingColDef={{
            sortable: false,
            minWidth: 300,
            headerName: "Name",
            renderCell: (params: GridRenderCellParams) => (
              <DataGridGroupingCell
                renderGroupCellElement={(value, node) => (
                  <GroupRow node={node} isNewlyAdded={isNewlyAdded}>
                    {renderGroupCellElement(value, node)}
                  </GroupRow>
                )}
                renderCellElement={(model) => renderGroupedRowCell(model as CommunicationMatrixModel)}
                {...params}
              />
            ),
            renderHeader: (params) => <DataGridGroupHeader params={params} />,
          }}
          sx={getCheckedGridSx}
          noRowsText={noItemsMessage}
          slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
          disableColumnReorder
          pinnedColumns={{
            left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
            right: ["actions"],
          }}
          onRowSelectionModelChange={(newModel) =>
            setSelectedIds(getSelectedRowIdsFromModel(newModel).filter((id) => !id.startsWith("auto-generated-row")))
          }
          isGroupExpandedByDefault={isGroupExpanded}
        />
      </DataGridHeaderToolbarProvider>

      <SendInvitationsConfirmationDialog
        open={invitationChanges.length > 0}
        onSubmit={handleSaveChanges}
        onClose={() => setInvitationChanges([])}
        changes={invitationChanges}
      />

      <ConcurrentModificationWarningDialog
        message={concurrentModificationMessage ?? ""}
        open={Boolean(concurrentModificationMessage)}
        onClose={handleConcurrentModificationClose}
      />

      {bulkPermissionsState && (
        <MatrixBulkEdit
          initialPermissionsState={bulkPermissionsState}
          recordsCount={selectedIds.length}
          onClose={() => setBulkPermissionsState(undefined)}
          categories={[...categories]}
          onSubmit={handleBulkEditSubmit}
        />
      )}
    </>
  );
};

export default CommunicationMatrix;

interface GroupRowProps {
  node: GridDataGroupNode | GridAutoGeneratedGroupNode | GridLeafNode;
  isNewlyAdded: (ids: string[]) => boolean;
}

const GroupRow = ({ node, isNewlyAdded, children }: PropsWithChildren<GroupRowProps>) => {
  const isGroup = node.type === "group";
  const isEmptypGroup = node.type === "leaf" && node.depth === 0;
  const isNew = !isEmptypGroup && isNewlyAdded(isGroup ? (node.children as string[]) : [node.id as string]);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", pl: isGroup ? 0 : 2 }}>
      {children}
      {isGroup && node.children && <BadgeDetached color="secondary" showZero badgeContent={node.children.length} />}
      {isNew && <NewLabel />}
    </Stack>
  );
};
